import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import trim from 'lodash/trim';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import {
  TextInput,
  PasswordInput,
  ManagedForm,
  CheckboxInput,
} from 'modules/ui';
import { useTealiumView, PLUS_SIGN_REGEX } from 'modules/accounts';

import { Validator } from '../services';
import {
  clearSignUpStatus,
  getSignupError,
  getSignupSuccess,
  signUp,
} from '../redux';
import {
  SignUpFormGrid,
  SignUpFormFooterGrid,
  AhabReCaptchaComponent,
} from '../components';
import classes from './SignUpPageForm.module.scss';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useNavigate, useParams } from 'react-router-dom';

const ACCOUNT_TYPES = {
  talent: {
    key: 'talent',
    title: 'Get started absolutely free',
    description: (
      <>
        <strong>Sign up as Talent</strong> and <strong>start </strong>
        auditioning for the roles
      </>
    ),
  },
  contentCreator: {
    key: 'content-creator',
    title: 'Create your account',
    description: (
      <>
        <strong>Sign up as Content Creator</strong> and
        <strong> build projects</strong> and <strong>auditions</strong>
      </>
    ),
  },
  agent: {
    key: 'agent',
    title: 'Find jobs for your clients',
    description: (
      <>
        <strong>Sign up as Agent</strong> and <strong>manage</strong>{' '}
        opportunities for your client list.
      </>
    ),
  },
};

function resolvePrefilledName(urlName) {
  let name = '';
  if (urlName) {
    name = urlName.replace(PLUS_SIGN_REGEX, ' ');
  }

  return name;
}

function resolveAccountType(accountType) {
  if (accountType === 'content-creator') {
    return 'producer';
  }
  return accountType;
}

export default function SignUpPage({ signupAccountType }) {
  const [email, setEmail] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const signupError = useSelector(getSignupError);
  const signupSuccess = useSelector(getSignupSuccess);

  const contentCreatorEmail = get(params, 'email');
  const contentCreatorFirstName = get(params, 'firstName');
  const contentCreatorLastName = get(params, 'lastName');

  const initialValues = {
    firstName: resolvePrefilledName(contentCreatorFirstName),
    lastName: resolvePrefilledName(contentCreatorLastName),
    email: contentCreatorEmail,
  };

  const [values, setValues] = useState(initialValues);

  useTealiumView('Signup Page');

  useEffect(() => () => dispatch(clearSignUpStatus()), [dispatch]);

  useEffect(() => {
    if (formSubmitted && signupSuccess) {
      navigate('/welcome', {
        state: { email },
      });
    }
  }, [formSubmitted, signupSuccess, email, navigate]);

  const getTitle = () => {
    const matchingAccount = find(
      ACCOUNT_TYPES,
      account => account.key === signupAccountType,
    );
    return matchingAccount.title;
  };

  const getDescription = () => {
    const matchingAccount = find(
      ACCOUNT_TYPES,
      account => account.key === signupAccountType,
    );
    return matchingAccount.description;
  };

  const [recaptchaToken, setRecaptchaToken] = useState('');

  const getRecaptchaToken = tokenData => {
    setRecaptchaToken(tokenData);
  };

  const handleSubmit = async () => {
    const { firstName, lastName } = values;

    const updatedUser = {
      ...values,
      lastName: trim(lastName),
      firstName: trim(firstName),
      accountType: resolveAccountType(signupAccountType),
      email: trim(values.email.toLowerCase()),
    };
    setEmail(updatedUser.email);
    setFormSubmitted(true);

    return dispatch(
      signUp({
        ...updatedUser,
        newsletterSignup: get(updatedUser, 'newsletterSignup', false),
        recaptchaToken: recaptchaToken,
      }),
    );
  };

  const handleChangeValue = data => {
    setValues(data);
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_SITE_KEY}>
      <ManagedForm
        title={getTitle()}
        description={getDescription()}
        footerDescription={
          'By clicking "Agree & Continue" I agree to Ahab’s Terms of Service and Privacy Policy.'
        }
        submitButtonLabel="Agree & Continue"
        onSubmit={handleSubmit}
        validate={Validator.signUpData}
        TemplateComponent={SignUpFormGrid}
        FooterTemplateComponent={SignUpFormFooterGrid}
        initialValues={initialValues}
        className={classes.form}
        error={signupError}
        onChangeValues={handleChangeValue}
        values={values}
      >
        <TextInput
          autoFocus
          label="First name"
          name="firstName"
          placeholder="E.g. Julie"
          id="first-name"
          required
        />
        <TextInput
          label="Last name"
          name="lastName"
          placeholder="E.g. Doe"
          id="last-name"
          required
        />
        <TextInput
          label="Email address"
          name="email"
          placeholder="julie@company.com"
          id="email"
          type="email"
          required
        />
        <PasswordInput
          showHint
          label="Add password"
          name="password"
          placeholder="Enter password"
          id="password"
          required
        />

        <CheckboxInput
          name="newsletterSignup"
          text="Sign up for news and other communications from Ahab"
        />
        <AhabReCaptchaComponent func={getRecaptchaToken} />
      </ManagedForm>
    </GoogleReCaptchaProvider>
  );
}

SignUpPage.propTypes = {
  signupAccountType: PropTypes.string.isRequired,
};
